import React, { useState, useEffect, useContext } from "react";
import { Box, Container, Chip, FormControl, InputLabel, Select, MenuItem, LinearProgress, Link, Typography, Avatar, Tab, Tabs } from "@mui/material";
import bkstApi from "../../common/component/api";
import OrderSummary from "../component/OrderSummary";
import ItemFilter from "../component/ItemFilter";
import { Link as RouterLink } from "react-router-dom";
import currency from "currency.js";
import moment from "moment";
import { isSuperAdmin, sortByOrderDateTime } from "../../common/component/util";
import { STORAGE_BUCKET } from "../../common/component/constants";
import RevealOnScroll from "../../common/component/animate/RevealOnScroll";
import CountUp from "../../common/component/animate/CountUp";
import ConfigContext from "../../common/component/context/ConfigContext";

const FILTERS = ["recent", "upcoming", "open"];

export default function OrderDashboard(props) {
  const ctx = useContext(ConfigContext);

  const [filter, setFilter] = useState("recent");
  const [limit, setLimit] = useState("1");

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [summary, setSummary] = useState();
  const [filteredSummary, setFilteredSummary] = useState();
  const [itemFilters, setItemFilters] = useState({});

  const [loading, setLoading] = useState(true);

  // Generate filter config from orders data
  const generateFilterConfig = (orders) => {
    const paymentTypeOptions = [...new Set(orders.map((o) => o.paymentType).filter(Boolean))];
    const fulfillmentTypeOptions = [...new Set(orders.map((o) => o.fulfillment?.type).filter(Boolean))];

    // Get store locations for pickup orders
    const storeOptions = [
      ...new Set(
        filteredOrders
          .filter((o) => o.fulfillment?.type === "pickup" && o.fulfillment?.location?.street)
          .map((o) => o.fulfillment.location.street)
          .filter(Boolean)
      )
    ];

    // Get delivery providers for delivery orders
    const providerOptions = [
      ...new Set(
        filteredOrders
          .filter((o) => o.fulfillment?.type === "delivery")
          .map((o) => o.fulfillment?.provider?.name || "in-house")
          .filter(Boolean)
      )
    ];

    // Get UTM values from orders
    const utmOptions = [
      ...new Set(
        filteredOrders
          .map((o) => {
            if (!o.utm) return null;
            // Handle both object with value property and string
            return `${o.utm.utms}:${o.utm.utmc}`;
          })
          .filter(Boolean)
      )
    ];

    // Get bakery names from orders
    const bakeryOptions = [...new Set(filteredOrders.map((o) => o.baker?.name).filter(Boolean))];

    return [
      {
        name: "payment",
        options: paymentTypeOptions
      },
      {
        name: "bakery",
        options: bakeryOptions
      },
      {
        name: "fulfillment",
        options: fulfillmentTypeOptions
      },
      {
        name: "utm",
        options: utmOptions
      },

      // Only show store filter if fulfillment type is pickup
      ...(itemFilters.fulfillment?.value === "pickup"
        ? [
            {
              name: "store",
              options: storeOptions
            }
          ]
        : []),
      // Only show provider filter if fulfillment type is delivery
      ...(itemFilters.fulfillment?.value === "delivery"
        ? [
            {
              name: "provider",
              options: providerOptions
            }
          ]
        : [])
    ];
  };

  // Calculate summary data from orders
  const calculateSummary = (orders) => {
    let smry = {
      mkt: { online: { count: 0, sales: 0 }, offline: { count: 0, sales: 0 } },
      saas: { online: { count: 0, sales: 0 }, offline: { count: 0, sales: 0 } },
      leaderboard: {}
    };

    for (const o of orders) {
      if (o.test || o.test === "true") {
        continue;
      }
      if (o.src === "mkt") {
        let data = smry["mkt"] || { online: { count: 0, sales: 0 } };
        data.online.count = +data.online.count + 1;
        data.online.sales = +data.online.sales + +o.payment.total;
        smry["mkt"] = data;
      } else if (o.src === "direct") {
        let data = smry["saas"] || { online: { count: 0, sales: 0 }, offline: { count: 0, sales: 0 } };
        let dataByAcct = smry["leaderboard"]?.[o.baker.id] || { online: { count: 0, sales: 0 }, offline: { count: 0, sales: 0 } };

        if (o.paymentType === "offline") {
          data.offline.count = +data.offline.count + 1;
          data.offline.sales = +data.offline.sales + +o.payment.total;

          dataByAcct.offline.count = +dataByAcct.offline.count + 1;
          dataByAcct.offline.sales = +dataByAcct.offline.sales + +o.payment.total;
        } else {
          data.online.count = +data.online.count + 1;
          data.online.sales = +data.online.sales + +o.payment.total;

          dataByAcct.online.count = +dataByAcct.online.count + 1;
          dataByAcct.online.sales = +dataByAcct.online.sales + +o.payment.total;
        }

        smry["saas"] = data;
        smry["leaderboard"][o.baker.id] = dataByAcct;
      }
    }

    smry.leaderboard = Object.entries(smry.leaderboard)
      .map(([k, v]) => {
        return {
          id: k,
          ...v
        };
      })
      .sort((o1, o2) => +o2.online.sales - +o1.online.sales);

    return smry;
  };

  // Filter orders based on item filters
  const applyItemFilters = (orders, filters) => {
    return orders.filter((order) => {
      // Payment filter
      if (filters.payment?.value && order.paymentType !== filters.payment.value) {
        return false;
      }

      // Fulfillment type filter
      if (filters.fulfillment?.value && order.fulfillment?.type !== filters.fulfillment.value) {
        return false;
      }

      // UTM filter
      if (filters.utm?.value) {
        if (!order.utm || `${order.utm.utms}:${order.utm.utmc}` !== filters.utm.value) {
          return false;
        }
      }

      // Bakery filter
      if (filters.bakery?.value) {
        if (!order.baker?.name || order.baker.name.toLowerCase() !== filters.bakery.value.toLowerCase()) {
          return false;
        }
      }

      // Store filter (only for pickup orders)
      if (filters.store?.value && filters.fulfillment?.value === "pickup") {
        if (order.fulfillment?.location?.street) {
          if (!order.fulfillment.location.street.toLowerCase().includes(filters.store.value.toLowerCase())) {
            return false;
          }
        } else if (order.fulfillment?.address) {
          if (!order.fulfillment.address.toLowerCase().includes(filters.store.value.toLowerCase())) {
            return false;
          }
        } else {
          return false;
        }
      }

      // Provider filter (only for delivery orders)
      if (filters.provider?.value && filters.fulfillment?.value === "delivery") {
        const orderProvider = order.fulfillment?.provider?.name || "in-house";
        if (orderProvider.toLowerCase() !== filters.provider.value.toLowerCase()) {
          return false;
        }
      }

      return true;
    });
  };

  // Apply filters whenever orders or itemFilters change
  useEffect(() => {
    const filtered = applyItemFilters(orders, itemFilters);
    setFilteredOrders(filtered);

    // Calculate summary based on filtered results
    const filteredSummaryData = calculateSummary(filtered);
    setFilteredSummary(filteredSummaryData);
  }, [orders, itemFilters]);

  useEffect(() => {
    let from = "";
    let to = "";

    if (filter === "recent") {
      from = moment().subtract(1, "days").format("YYYY-MM-DD");
    } else if (filter === "open") {
    } else if (filter === "upcoming") {
      from = moment().format("YYYY-MM-DD");
      to = moment().add(2, "days").format("YYYY-MM-DD");
    } else {
      from = moment().format("YYYY-MM-DD");
      to = moment().add(28, "days").endOf("isoweek").format("YYYY-MM-DD");
    }

    setFromDt(from);
    setToDt(to);

    search(from, to);
  }, [filter]);

  const search = (from, to, limit) => {
    setLoading(true);

    bkstApi.get(`/admin/order?filter=${filter}&fromDt=${from}&toDt=${to}&limit=${limit || ""}`).then((res) => {
      let sortedOrders;
      if (filter === "recent") {
        //sort to make pending order appear on top
        sortedOrders = res.data.sort((a, b) => (a.status === "pending" ? -1 : 1));
      } else {
        sortedOrders = res.data.sort(sortByOrderDateTime);
      }

      setOrders(sortedOrders);
      setLimit(limit || 1);

      // Calculate initial summary data
      const initialSummary = calculateSummary(sortedOrders);
      setSummary(initialSummary);
      setFilteredSummary(initialSummary);

      setLoading(false);
    });
  };

  const handleFilterChange = (newFilters) => {
    let tmp = { ...itemFilters, ...newFilters };

    // Clear out store location filter if fulfillment is not pickup
    if (tmp["store"]?.value) {
      if (tmp["fulfillment"]?.value !== "pickup") {
        delete tmp["store"];
      }
    }

    // Clear out fulfillment provider if type is not delivery
    if (tmp["provider"]?.value) {
      if (tmp["fulfillment"]?.value !== "delivery") {
        delete tmp["provider"];
      }
    }

    setItemFilters(tmp);
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    let action = "";
    if (filter === "recent") {
      action = (
        <FormControl variant="filled">
          <InputLabel id="limit-label">Past</InputLabel>
          <Select
            value={limit || ""}
            labelId="past-label"
            label="Past"
            onChange={(e) => {
              setLimit(e.target.value);
              search("", "", e.target.value);
            }}
            size="small"
          >
            <MenuItem value={"1"}>1 Hour</MenuItem>
            <MenuItem value={"8"}>8 Hours</MenuItem>
            <MenuItem value={"24"}>24 Hours</MenuItem>
          </Select>
        </FormControl>
      );
    }

    const filterConfig = generateFilterConfig(orders);
    const hasActiveFilters = Object.keys(itemFilters).length > 0 && Object.values(itemFilters).some((f) => f?.value);

    return (
      <Box py={4}>
        <Container maxWidth="md">
          <Box>
            <Typography variant="h1" gutterBottom>
              Orders
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: "90vw" }}>
              <Tabs value={filter} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ borderBottom: "1px solid #ECECEC" }}>
                {FILTERS.map((o, idx) => {
                  return <Tab key={idx} value={o} label={o} onClick={() => setFilter(o)} />;
                })}
              </Tabs>

              {action}
            </Box>
          </Box>

          {filteredSummary && (
            <Box my={1} align="center">
              <Box sx={{ display: "flex", gap: "15px" }}>
                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="h4">
                    <CountUp from={0} to={filteredSummary.saas.online.sales} currencyOptions={{ precision: 0 }}></CountUp>
                  </Typography>
                  <Typography variant="overline" color="textSecondary">
                    SALES
                  </Typography>
                  <Typography variant="overline" color="error" display="block">
                    {currency(filteredSummary.saas.offline.sales, { precision: 0 }).format()}
                  </Typography>
                </Box>
                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="h4">{filteredSummary.saas.online.count}</Typography>
                  <Typography variant="overline" color="textSecondary">
                    ORDERS
                  </Typography>
                  <Typography variant="overline" color="error" display="block">
                    {filteredSummary.saas.offline.count}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Platform Fee Summary, AI Fee Summary, DSP Fee Summary */}
          {isSuperAdmin(ctx.uid) && filteredOrders.length > 0 && (
            <Box>
              <Box my={1} align="center" sx={{ display: "flex", gap: "15px" }}>
                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="overline" color="textSecondary">
                    PAYMENT FEE*
                  </Typography>
                  <Typography variant="h5">
                    {currency(
                      filteredOrders
                        .filter((o) => !o.payment?.platformFee && o.baker?.id !== "99i69dd8bwTqlxzVLZUx" && o.paymentType !== "offline")
                        .reduce((sum, o) => sum + (o.payment && o.payment.total ? 0.015 * +o.payment.total : 0), 0),
                      { precision: 0 }
                    ).format()}
                  </Typography>
                </Box>
                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="overline" color="textSecondary">
                    PLATFORM FEE
                  </Typography>
                  <Typography variant="h5">
                    {currency(
                      filteredOrders.reduce((sum, o) => sum + (o.payment && o.payment.platformFee ? +o.payment.platformFee : 0), 0),
                      { precision: 0 }
                    ).format()}
                  </Typography>
                </Box>

                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="overline" color="textSecondary">
                    DSP FEE*
                  </Typography>
                  <Typography variant="h5">
                    {currency(filteredOrders.filter((o) => o.fulfillment && o.fulfillment.provider?.name === "doordash").length * 5, {
                      precision: 0
                    }).format()}
                  </Typography>
                </Box>
                <Box p={2} sx={{ backgroundColor: "#EEE", flexGrow: 1 }}>
                  <Typography variant="overline" color="textSecondary">
                    AI FEE*
                  </Typography>
                  <Typography variant="h5">
                    {currency(
                      filteredOrders
                        .filter((o) => o.autoQuote === true)
                        .reduce((sum, o) => sum + (o.payment && o.payment.total ? 0.05 * +o.payment.total : 0), 0),
                      { precision: 0 }
                    ).format()}
                  </Typography>
                </Box>
              </Box>
              <Box textAlign={"center"}>
                <Typography variant="caption" color="textSecondary">
                  *Approximate total, not precise
                </Typography>
              </Box>
            </Box>
          )}

          <Box mb={2} sx={{ display: "flex", gap: "15px", overflowX: "auto", maxWidth: "85vw", border: "1px solid #ECECEC", p: 2 }}>
            {filteredSummary?.leaderboard?.map((o) => (
              <Box key={o.id} sx={{ textAlign: "center" }}>
                <Link component={RouterLink} to={`/baker/${o.id}`}>
                  <Avatar src={`${STORAGE_BUCKET}/img/b/${o.id}/logo400.webp`} sx={{ width: "50px", height: "50px" }} />
                </Link>
                <Box py={0.3}>
                  <Typography sx={{ fontSize: "9px" }}>
                    {currency(o.online.sales, { precision: 0 }).format()} ({o.online.count})
                  </Typography>
                </Box>
                {+o.offline.count > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: "9px", color: "red" }}>
                      {currency(o.offline.sales, { precision: 0 }).format()} ({o.offline.count})
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Item Filter */}
          <ItemFilter config={filterConfig} onChange={handleFilterChange} />

          {/* Show filtered results count */}
          {hasActiveFilters && (
            <Box my={2}>
              <Typography variant="body2" color="textSecondary">
                Showing {filteredOrders.length} of {orders.length} orders
              </Typography>
            </Box>
          )}

          {filteredOrders.map((o) => {
            return (
              <Box my={2} key={o.id}>
                <Link underline="none" component={RouterLink} to={`/order/${o.id}`} href="#">
                  <RevealOnScroll>
                    <OrderSummary order={o} />
                  </RevealOnScroll>
                </Link>
              </Box>
            );
          })}
        </Container>
      </Box>
    );
  }
}
